import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  Injector,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { PaginatorModule } from 'primeng/paginator';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { DictionaryItem } from '@services/dictionaries-api.service';
import { BaseService } from '@6grain/masofa-monolith-api-client/dictionaries/api.base.service';
import { SystemDocumentationBlockService } from '@6grain/masofa-monolith-api-client/dictionaries/api/systemDocumentationBlock.service';
import { ServerTranslateService } from '@services/server-translate.service';
import { TableConfig } from '@components/data-table/data-table.component';
import { BackButton } from '@components/ui/back-button/back-button';
import { Filters } from '@components/views/filters/filters';
import { HttpClient } from '@angular/common/http';
import { environment } from '@root/src/environments/environment';
import { AddSystemDocumentationBlockForm } from './forms/add-system-documentation-block-form/add-system-documentation-block-form';
import {
  ListTableWithoutFiltersWrapper
} from '@app/wrappers/pages/list-table-without-filters-wrapper/list-table-without-filters-wrapper';
import { buildFilterQuery } from '@app/utils/_msf';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { FilterOperator as FilterOperatorLocal } from '@app/utils/filter-operator';

interface TableItem {
  header: string;
  field: string;
  metaField?: string;
  isBoolean?: boolean;
  isDate?: boolean;
  parseFunction?: (field: any) => string;
  template?: string;
  type?: string;
  customTemplate?: TemplateRef<unknown>;
}

@Component({
  selector: 'app-system-documentation-blocks-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    CheckboxModule,
    ProgressSpinnerModule,
    CardModule,
    DialogModule,
    TooltipModule,
    PaginatorModule,
    TranslateModule,
    BackButton,
    Filters,
    ToastModule,
    ListTableWithoutFiltersWrapper,
  ],
  providers: [MessageService, DialogService, DateFormatLangPipe, DatePipe],
  templateUrl: './system-documentation-blocks-page.html',
  styleUrls: ['./system-documentation-blocks-page.scss']
})
export class SystemDocumentationBlocksPageComponent implements OnInit, AfterViewInit {
  private readonly router: Router;
  private readonly destroyRef: DestroyRef;
  public readonly translateService: TranslateService;
  private readonly serverTranslateService: ServerTranslateService;
  private readonly http: HttpClient;
  private readonly messageService: MessageService;
  private readonly dialogService: DialogService;
  private readonly systemDocumentationBlockService: SystemDocumentationBlockService;

  // View templates
  @ViewChild('actionsTemplate') actionsTemplate!: TemplateRef<unknown>;
  @ViewChild('nameTemplate') nameTemplate!: TemplateRef<unknown>;
  @ViewChild('parentBlockTemplate') parentBlockTemplate!: TemplateRef<unknown>;

  readonly loading = signal<boolean>(true);
  readonly items = signal<DictionaryItem[]>([]);
  readonly totalRecords = signal<number>(0);
  readonly showModal = signal<boolean>(false);
  readonly isSaving = signal<boolean>(false);
  readonly showDeleteModal = signal<boolean>(false);
  readonly isDeleting = signal<boolean>(false);
  readonly lastUpdateTime = signal<Date | null>(null);
  readonly systemDocumentationBlocks = signal<any[]>([]);
  readonly metaData = signal<any>({});

  // State
  template: any = {
    title: 'System Documentation Blocks',
    service: SystemDocumentationBlockService,
    queryPostMethod: 'dictionariesSystemDocumentationBlockGetByQueryPost',
    table: [
      {
        header: 'Name',
        field: 'names'
      },
      {
        header: 'Parent Block',
        field: 'parentId',
        metaField: 'systemDocumentationBlocks'
      },
      {
        header: 'Order Code',
        field: 'orderCode'
      }
    ],
    filters: [
      {
        name: 'names',
        title: 'Name',
        type: 'input'
      },
      {
        name: 'parentId',
        title: 'Parent Block',
        type: 'select',
        metaField: 'systemDocumentationBlocks'
      }
    ],
    meta: {
      systemDocumentationBlocks: {
        field: [],
        displayField: 'names',
        service: SystemDocumentationBlockService
      }
    }
  };
  filters: any = {};
  parentRoute = '/admin';
  first = 0;
  take = 10;
  modalData: any = {};
  editingItem: DictionaryItem | null = null;
  itemToDelete: DictionaryItem | null = null;
  filtersParams: Object[] = [];
  toggleFilter = false;
  apiService!: BaseService;
  ref: DynamicDialogRef | undefined;

  tableConfig: TableConfig = {
    selectable: false,
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [10, 25, 50, 100],
    loading: false,
    emptyMessage: 'No data',
  };

  constructor(
    public injector: Injector
  ) {
    this.router = inject(Router);
    this.destroyRef = inject(DestroyRef);
    this.translateService = inject(TranslateService);
    this.serverTranslateService = inject(ServerTranslateService);
    this.http = inject(HttpClient);
    this.messageService = inject(MessageService);
    this.dialogService = inject(DialogService);
    this.systemDocumentationBlockService = inject(SystemDocumentationBlockService);
  }

  ngOnInit(): void {
    this.initializeComponent();
    this.setupLanguageChangeListener();
    this.loadSystemDocumentationBlocks();
  }

  ngAfterViewInit(): void {
    this.setupTableTemplates();
  }

  private initializeComponent(): void {
    this.apiService = this.systemDocumentationBlockService;
    this.setupMultilingualColumns();
    this.tableConfig.emptyMessage = this.translateService.instant('No data');
  }

  private loadSystemDocumentationBlocks(): void {
    const apiUrl = `${environment.api}/dictionaries/SystemDocumentationBlock/GetByQuery`;

    this.http.post<any[]>(apiUrl, {
      offset: 0,
      take: 9999,
      filters: []
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const blocks = response || [];
          this.systemDocumentationBlocks.set(blocks);
          // Обновляем metaData для передачи в Filters компонент
          this.metaData.set({
            systemDocumentationBlocks: blocks
          });
        },
        error: (error) => {
          console.error('Error loading system documentation blocks:', error);
          this.systemDocumentationBlocks.set([]);
          this.metaData.set({
            systemDocumentationBlocks: []
          });
        }
      });
  }

  private setupLanguageChangeListener(): void {
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.tableConfig.emptyMessage = this.translateService.instant('No data');
      });
  }

  applyFilters(): void {
    this.filtersParams = buildFilterQuery(this.filters);

    this.filtersParams.forEach((param: any) => {
      if ((param.filterField === 'names' || param.filterField === 'fullName') && param.filterValue) {
        param.filterOperator = FilterOperatorLocal.Contains;
      }
    });

    this.toggleFilter = !this.toggleFilter;
  }

  clearFilters(): void {
    this.filters = {};
    this.filtersParams = buildFilterQuery(this.filters);
    this.toggleFilter = !this.toggleFilter;
  }

  openAddModal(): void {
    this.openSystemDocumentationBlockModal('create');
  }

  openEditModal(item: DictionaryItem): void {
    this.openSystemDocumentationBlockModal('edit', item);
  }

  private openSystemDocumentationBlockModal(mode: 'create' | 'edit', model?: DictionaryItem): void {
    this.ref = this.dialogService.open(AddSystemDocumentationBlockForm, {
      header: this.translateService.instant(mode === 'create' ? 'Add system documentation block' : 'Edit system documentation block'),
      width: '50vw',
      height: 'auto',
      closable: true,
      modal: true,
      data: { mode, model }
    });

    this.ref.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.success) {
          this.loadSystemDocumentationBlocks(); // Обновляем блоки после сохранения
          this.loadData();
        }
      });
  }

  deleteItem(item: DictionaryItem): void {
    this.itemToDelete = item;
    this.showDeleteModal.set(true);
  }

  confirmDelete(): void {
    if (!this.itemToDelete) return;

    this.isDeleting.set(true);
    this.systemDocumentationBlockService
      .dictionariesSystemDocumentationBlockDeleteIdDelete(this.itemToDelete.id!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isDeleting.set(false);
          this.showDeleteModal.set(false);
          this.itemToDelete = null;
          this.loadData();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Success'),
            detail: this.translateService.instant('Item deleted successfully')
          });
        },
        error: (error) => {
          this.isDeleting.set(false);
          console.error('Error deleting item:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('Error'),
            detail: this.translateService.instant('Failed to delete item')
          });
        }
      });
  }

  cancelDelete(): void {
    this.showDeleteModal.set(false);
    this.itemToDelete = null;
  }

  exportData(): void {
    if (!this.items()?.length) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('Warning'),
        detail: this.translateService.instant('No data to export')
      });
      return;
    }

    const exportParams: any = {
      offset: 0,
      take: 999999,
      filters: buildFilterQuery(this.filters)
    };

    this.loading.set(true);
    this.systemDocumentationBlockService
      .dictionariesSystemDocumentationBlockGetByQueryPost(exportParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const allItems = response || [];
          const csvData = this.convertToCSV(allItems);
          this.downloadCSV(csvData, `system-documentation-blocks_${new Date().toISOString().split('T')[0]}.csv`);
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Success'),
            detail: this.translateService.instant('Data exported successfully')
          });
        },
        error: (error) => {
          console.error('Error exporting data:', error);
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('Error'),
            detail: this.translateService.instant('Failed to export data')
          });
        }
      });
  }

  private downloadCSV(csvData: string, filename: string): void {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private convertToCSV(items: any[]): string {
    if (!items?.length) return '';

    const headers: string[] = [];
    const fields: string[] = [];

    this.template.table
      .filter((col: TableItem) => col.field && col.header !== 'Actions')
      .forEach((col: TableItem) => {
        if (col.field === 'names') {
          environment.langs.forEach(langObj => {
            const lang =  langObj.value;
            headers.push(`Name (${this.getLanguageLabel(lang)})`);
            fields.push(`name_${lang}`);
          });
        } else {
          headers.push(col.header);
          fields.push(col.field as string);
        }
      });

    let csv = headers.join(',') + '\n';

    items.forEach(item => {
      const row = fields.map((field: string) => {
        let value: string | number | boolean | null | undefined;

        if (field.startsWith('name_')) {
          const langCode = field.replace('name_', '');
          value = item['names']?.[langCode] || '';
        } else if (field === 'parentId') {
          const parentBlock = this.systemDocumentationBlocks().find(b => b.id === item[field]);
          value = parentBlock ? this.getNameForLanguage(parentBlock) : item[field] || '';
        } else {
          value = item[field];
        }

        if (value === null || value === undefined) {
          return '""';
        } else if (typeof value === 'boolean') {
          return `"${value ? 'Yes' : 'No'}"`;
        } else {
          return `"${String(value).replace(/"/g, '""')}"`;
        }
      });

      csv += row.join(',') + '\n';
    });

    return '\uFEFF' + csv;
  }

  private setupMultilingualColumns(): void {
    const newColumns: any[] = [];

    this.template.table.forEach((column: any) => {
      if (column.field === 'name' || column.field === 'names') {
        environment.langs.forEach((langObj) => {
          const lang =  langObj.value;
          newColumns.push({
            ...column,
            field: `name_${lang}`,
            header: `Name (${this.getLanguageLabel(lang)})`,
            type: 'text',
            isMultilingual: true,
            originalField: column.field,
            language: lang
          });
        });
      } else {
        if (!column.type) {
          column.type = column.isBoolean ? 'boolean' : column.isDate ? 'date' : 'text';
        }
        newColumns.push(column);
      }
    });

    this.template.table = newColumns;
  }

  private getLanguageLabel(lang: string): string {
    const labels: Record<string, string> = {
      'ru-RU': 'RU',
      'en-US': 'EN',
      'uz-Cyrl-UZ': 'UZ',
      'uz-Latn-UZ': 'UZ'
    };
    return labels[lang] || lang;
  }

  private setupTableTemplates(): void {
    const actions: TableItem = {
      field: '',
      header: 'Actions',
      type: 'custom',
      customTemplate: this.actionsTemplate,
    };

    if (this.template.table && !this.template.table.some((item: any) => item.header === 'Actions')) {
      this.template.table.push(actions);
    }

    this.template.table?.forEach((item: any) => {
      if ((item.field === 'name' || item.field === 'names') && this.nameTemplate) {
        item.type = 'custom';
        item.customTemplate = this.nameTemplate;
      } else if (item.isMultilingual && this.nameTemplate) {
        item.type = 'custom';
        item.customTemplate = this.nameTemplate;
      } else if (!item.type) {
        item.type = 'text';
      }

      // Handle parentId column with metaField
      if (item.field === 'parentId' && item.metaField === 'systemDocumentationBlocks' && this.parentBlockTemplate) {
        item.type = 'custom';
        item.customTemplate = this.parentBlockTemplate;
      }
    });
  }

  getParentBlockName(parentId: string | null | undefined): string {
    if (!parentId) return '-';

    const block = this.systemDocumentationBlocks().find(b => b.id === parentId);
    if (!block) return parentId;

    return this.getNameForLanguage(block);
  }

  getNameForLanguage(item: any): string {
    if (!item?.names) return '';

    const langMap: Record<string, string> = {
      'en': 'en-US',
      'en-us': 'en-US',
      'ru': 'ru-RU',
      'ru-ru': 'ru-RU',
      'uz': 'uz-Latn-UZ',
      'uz-latn': 'uz-Latn-UZ'
    };

    const currentLang = this.translateService.currentLang;
    const langCode = langMap[currentLang?.toLowerCase()] || '';

    return item.names[langCode] || item.names['en-US'] || item.names['ru-RU'] || item.names['uz-Latn-UZ'] || '';
  }

  getMultilingualValue(item: any, fieldName: string): string {
    if (!item?.names) return '';
    const language = fieldName.replace('name_', '');
    try {
      const names = typeof item.names === 'string' ? JSON.parse(item.names) : item.names;
      return names[language] || '';
    } catch (e) {
      console.warn('Error parsing multilingual value', e);
      return '';
    }
  }

  getDictApiMethodName(apiMethodName: string): string {
    const entityName = 'SystemDocumentationBlock';

    const methodMap: Record<string, string> = {
      'CreatePost': `dictionaries${entityName}CreatePost`,
      'UpdatePut': `dictionaries${entityName}UpdatePut`,
      'DeleteIdDelete': `dictionaries${entityName}DeleteIdDelete`,
      'GetByQueryPost': `dictionaries${entityName}GetByQueryPost`,
      'total': `dictionaries${entityName}GetTotalCountPost`
    };

    let methodName = methodMap[apiMethodName] || `dictionaries${entityName}${apiMethodName}`;

    if (this.template?.queryPostMethod && apiMethodName === 'GetByQueryPost') {
      methodName = this.template.queryPostMethod;
    }

    return methodName;
  }

  private loadData(): void {
    this.toggleFilter = !this.toggleFilter;
    this.lastUpdateTime.set(new Date());
  }
}

